<script setup>
import { computed, ref, watch } from 'vue';
import { useMessageContext } from '../provider.js';
import BaseBubble from './Base.vue';

const { content, additionalAttributes } = useMessageContext();

const MAX_HEADER_SIZE = 330;
const MIN_HEADER_SIZE = 240;

const templateParams = computed(
  () =>
    additionalAttributes.value?.template_params ||
    additionalAttributes.value?.templateParams ||
    {}
);

const processedParams = computed(
  () => templateParams.value.processed_params || {}
);

console.debug('[NextBubbleWhatsappTemplate] mounting', {
  templateParams: templateParams.value,
});

const headerData = computed(() => processedParams.value.header || {});

const headerMedia = computed(() => {
  const type = headerData.value.media_type;
  if (headerData.value.media_url && type) {
    return { type: type.toLowerCase(), url: headerData.value.media_url };
  }
  if (headerData.value.media_id && type) {
    return { type: type.toLowerCase(), id: headerData.value.media_id };
  }
  return null;
});

const footerText = computed(() => {
  // Footer is stored at templateParams level (not in processed_params)
  return templateParams.value?.footer || '';
});

const buttons = computed(() => {
  // Buttons are stored at templateParams level (not in processed_params)
  if (Array.isArray(templateParams.value?.buttons)) {
    return templateParams.value.buttons;
  }
  return [];
});

const bodyContent = computed(() => {
  let text = content.value || '';
  // Convert <br>, <br/>, <br /> to newlines
  text = text.replace(/<br\s*\/?>/gi, '\n');
  // Remove HTML tags
  text = text.replace(/<[^>]*>/g, '');
  // Decode HTML entities
  text = text.replace(/&nbsp;/g, ' ');
  text = text.replace(/&amp;/g, '&');
  text = text.replace(/&lt;/g, '<');
  text = text.replace(/&gt;/g, '>');
  text = text.replace(/&quot;/g, '"');
  // Normalize multiple newlines to single newline
  text = text.replace(/\n{2,}/g, '\n');
  // Trim extra whitespace
  return text.trim();
});

const getButtonIcon = button => {
  const type = button.type?.toUpperCase();
  if (type === 'URL' || button.url) return 'i-lucide-external-link';
  if (type === 'PHONE_NUMBER' || button.phone_number) return 'i-lucide-phone';
  if (type === 'COPY_CODE') return 'i-lucide-copy';
  return 'i-lucide-reply';
};

const getButtonText = button => {
  return button.text || button.title || button.parameter || button.payload || 'Botão';
};

const getButtonLink = button => {
  const type = button.type?.toUpperCase();
  if (type === 'URL' || button.url) {
    return button.url;
  }
  if (type === 'PHONE_NUMBER' || button.phone_number) {
    const phone = button.phone_number || button.url;
    return phone ? `tel:${phone}` : null;
  }
  return null;
};

const handleButtonClick = button => {
  const type = button.type?.toUpperCase();
  if (type === 'COPY_CODE') {
    const code = button.example || button.text || '';
    navigator.clipboard.writeText(code);
  }
};

const mediaBox = ref({
  width: MAX_HEADER_SIZE,
  height: MAX_HEADER_SIZE,
  objectFit: 'contain',
});

const computeMediaBox = (naturalWidth, naturalHeight) => {
  if (!naturalWidth || !naturalHeight) {
    return {
      width: MAX_HEADER_SIZE,
      height: MAX_HEADER_SIZE,
      objectFit: 'contain',
    };
  }

  const scaleToMax = Math.min(
    MAX_HEADER_SIZE / naturalWidth,
    MAX_HEADER_SIZE / naturalHeight,
    1
  );

  let width = naturalWidth * scaleToMax;
  let height = naturalHeight * scaleToMax;

  const isBelowMin = naturalWidth < MIN_HEADER_SIZE || naturalHeight < MIN_HEADER_SIZE;

  if (isBelowMin) {
    const scaleToMin = Math.max(
      MIN_HEADER_SIZE / width,
      MIN_HEADER_SIZE / height,
      1
    );

    width = Math.min(width * scaleToMin, MAX_HEADER_SIZE);
    height = Math.min(height * scaleToMin, MAX_HEADER_SIZE);
  }

  return {
    width: Math.max(Math.round(width), 1),
    height: Math.max(Math.round(height), 1),
    objectFit: isBelowMin ? 'cover' : 'contain',
  };
};

const updateMediaBox = ({ width, height }) => {
  mediaBox.value = computeMediaBox(width, height);
};

const onImageLoad = event => {
  updateMediaBox({
    width: event?.target?.naturalWidth,
    height: event?.target?.naturalHeight,
  });
};

const onVideoMetadata = event => {
  updateMediaBox({
    width: event?.target?.videoWidth,
    height: event?.target?.videoHeight,
  });
};

const mediaBoxStyle = computed(() => ({
  width: `${mediaBox.value.width}px`,
  height: `${mediaBox.value.height}px`,
}));

const mediaObjectFit = computed(() => mediaBox.value.objectFit);

const bubbleStyle = computed(() => {
  if (!headerMedia.value) return {};
  return {
    width: `${mediaBox.value.width}px`,
    maxWidth: `${MAX_HEADER_SIZE}px`,
  };
});

watch(
  () => headerMedia.value,
  () => {
    mediaBox.value = {
      width: MAX_HEADER_SIZE,
      height: MAX_HEADER_SIZE,
      objectFit: 'contain',
    };
  }
);
</script>

<template>
  <BaseBubble
    class="wa-template-bubble"
    data-bubble-name="wa-template"
    :style="bubbleStyle"
  >
    <!-- Header com mídia -->
    <div v-if="headerMedia" class="wa-header">
      <div
        v-if="headerMedia.type === 'image' && headerMedia.url"
        class="wa-header-media-container"
        :style="mediaBoxStyle"
      >
        <img
          :src="headerMedia.url"
          alt="Header media"
          class="wa-header-media"
          :style="{ objectFit: mediaObjectFit }"
          @load="onImageLoad"
        />
      </div>
      <div
        v-else-if="headerMedia.type === 'video' && headerMedia.url"
        class="wa-header-media-container"
        :style="mediaBoxStyle"
      >
        <video
          :src="headerMedia.url"
          controls
          class="wa-header-media"
          :style="{ objectFit: mediaObjectFit }"
          @loadedmetadata="onVideoMetadata"
        />
      </div>
      <div v-else-if="headerMedia.type === 'document'" class="wa-header-document">
        <i class="i-lucide-file-text text-xl text-n-slate-11" />
        <span class="text-sm text-n-slate-11">Documento</span>
        <span v-if="headerMedia.id" class="text-xs text-n-slate-10">ID: {{ headerMedia.id }}</span>
      </div>
      <div v-else class="wa-header-placeholder">
        <i class="i-lucide-image text-xl text-n-slate-10" />
        <span>{{ headerMedia.type?.toUpperCase() || 'MÍDIA' }}</span>
        <span v-if="headerMedia.id" class="wa-header-id">ID: {{ headerMedia.id }}</span>
      </div>
    </div>

    <!-- Corpo do template -->
    <div v-if="bodyContent" class="wa-body">
      {{ bodyContent }}
    </div>

    <!-- Footer -->
    <div v-if="footerText" class="wa-footer">
      <span class="wa-footer-text">{{ footerText }}</span>
    </div>

    <!-- Botões -->
    <div v-if="buttons.length" class="wa-buttons">
      <a
        v-for="(button, index) in buttons"
        :key="index"
        :href="getButtonLink(button)"
        :target="button.type?.toUpperCase() === 'URL' ? '_blank' : undefined"
        rel="noopener noreferrer"
        class="wa-button"
        @click="handleButtonClick(button)"
      >
        <i :class="getButtonIcon(button)" class="wa-button-icon" />
        <span class="wa-button-text">{{ getButtonText(button) }}</span>
      </a>
    </div>
  </BaseBubble>
</template>

<style scoped>
/* Bubble — paleta oficial do WhatsApp outgoing */
.wa-template-bubble {
  @apply flex flex-col;
  @apply bg-[#D9FDD3] dark:bg-[#005C4B];
  @apply text-[#111B21] dark:text-[#E9EDEF];
  max-width: 360px;
  min-width: 240px;
}

/* Header — edge-to-edge no topo da bolha */
.wa-header {
  @apply overflow-hidden rounded-t-xl flex items-center justify-center;
}

.wa-header-media-container {
  max-width: 360px;
  max-height: 360px;
  @apply flex items-center justify-center overflow-hidden;
}

.wa-header-media {
  width: 100%;
  height: 100%;
  max-width: 360px;
  max-height: 360px;
  @apply block;
}

.wa-header-document,
.wa-header-placeholder {
  @apply flex flex-col items-center justify-center gap-1 py-8 px-4;
  @apply text-[#667781] dark:text-[#8696A0];
  background-color: rgba(0, 0, 0, 0.04);
}

:global(.dark) .wa-header-document,
:global(.dark) .wa-header-placeholder {
  background-color: rgba(255, 255, 255, 0.06);
}

.wa-header-placeholder {
  @apply text-sm;
}

.wa-header-id {
  @apply block text-xs mt-1 opacity-70;
}

/* Body — escala do WhatsApp: 14.2px / 19px */
.wa-body {
  @apply px-3 pt-3 whitespace-pre-wrap text-[14.2px] leading-[19px];
}

/* Footer — pequeno e suave */
.wa-footer {
  @apply px-3 pt-1 pb-2;
}

.wa-footer-text {
  @apply text-[13px] text-[#667781] dark:text-[#8696A0];
}

/* Botões — estilo WhatsApp: texto colorido + divisor, sem fill */
.wa-buttons {
  @apply flex flex-col mt-1;
}

.wa-button {
  @apply flex items-center justify-center gap-2 w-full;
  @apply px-3 py-2.5 text-sm font-medium;
  @apply text-[#027EB5] dark:text-[#53BDEB];
  @apply no-underline cursor-pointer transition-colors duration-150;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

:global(.dark) .wa-button {
  border-top-color: rgba(255, 255, 255, 0.10);
}

.wa-button-icon {
  @apply w-4 h-4 flex-shrink-0;
}

.wa-button-text {
  @apply truncate;
}

.wa-button:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.wa-button:active {
  background-color: rgba(0, 0, 0, 0.08);
}

:global(.dark) .wa-button:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

:global(.dark) .wa-button:active {
  background-color: rgba(255, 255, 255, 0.10);
}
</style>
